/* lang-script.js — English pronunciation respelled in the reader's own
 * alphabet ("русскими буквами", pronunciación figurada, 한글 발음…), for the
 * pronunciation-respelling tool.
 *
 * Input is IPA as LangIPA / LangConnected print it: primary ˈ and secondary ˌ
 * stress marks at the start of a syllable, ː after a long vowel. Output is a
 * list of SEGMENTS — one per English sound — so the page can mark the letters
 * that only approximate a sound the target language does not have, and a
 * flat string for copying.
 *
 * v2 (2026-09-24). v1 mapped one IPA unit to one letter and skipped anything
 * it did not know; measured on real words that meant:
 *   · British r was never written (LangIPA prints /r/, the tables knew only
 *     /ɹ/): right «айт», red «эд», very «вэи», run «ан» — in the default mode;
 *   · no stress, no length: ship and sheep were both «шип»;
 *   · j glued onto the next letter: music «мйузик», you «йу», new «нйу»;
 *   · would «ууд», think «сингк», table «тэйбэл».
 * Each of those is a rule below, with the case that proves it in
 * tests/js/lang-script.test.mjs.
 *
 * Plain IIFE, no dependencies. Exposes window.LangScript + module.exports.
 */
(function (root, factory) {
  'use strict';
  var L = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = L;
  if (typeof window !== 'undefined') window.LangScript = L;
  else if (root) root.LangScript = L;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var ACUTE = '\u0301';

  /* IPA units, longest first. British (RP) and American sets together. */
  var UNITS = ['aɪə', 'aʊə', 'ɔɪə',
    'aʊ', 'aɪ', 'eɪ', 'oʊ', 'ɔɪ', 'əʊ', 'eə', 'ɪə', 'ʊə', 'tʃ', 'dʒ',
    'ɑ', 'æ', 'ʌ', 'ə', 'ɔ', 'ɛ', 'ɝ', 'ɚ', 'ɪ', 'i', 'ʊ', 'u', 'ɒ', 'e', 'ɜ',
    'b', 'd', 'ð', 'f', 'ɡ', 'g', 'h', 'k', 'l', 'm', 'n', 'ŋ', 'p',
    'ɹ', 'r', 's', 'ʃ', 't', 'θ', 'v', 'w', 'j', 'z', 'ʒ'];
  var VOWEL = {};
  ['aɪə', 'aʊə', 'ɔɪə', 'aʊ', 'aɪ', 'eɪ', 'oʊ', 'ɔɪ', 'əʊ', 'eə', 'ɪə', 'ʊə',
    'ɑ', 'æ', 'ʌ', 'ə', 'ɔ', 'ɛ', 'ɝ', 'ɚ', 'ɪ', 'i', 'ʊ', 'u', 'ɒ', 'e', 'ɜ'
  ].forEach(function (u) { VOWEL[u] = 1; });

  /* One canonical id per English sound family — the page keys its tips on
   * these, so a table never has to know the UI language. */
  var SOUND_ID = { 'θ': 'th', 'ð': 'dh', 'w': 'w', 'æ': 'ae', 'ŋ': 'ng',
    'ɜ': 'er', 'ɝ': 'er', 'h': 'h', 'ɹ': 'r', 'r': 'r', 'ʌ': 'uh', 'ɪ': 'ih',
    'v': 'v', 'z': 'z', 'ʃ': 'sh', 'ʒ': 'zh', 'dʒ': 'j', 'tʃ': 'ch', 'p': 'p',
    'ə': 'schwa', 'ɒ': 'o', 'ɔ': 'aw', 'ʊ': 'uu', 'e': 'e', 'ɛ': 'e', 'ɡ': 'g', 'g': 'g' };

  /* ── parsing ───────────────────────────────────────────────────── */
  /* → [{u, v, st, long}] — st 1 primary, 2 secondary, 0 none (per vowel). */
  function parse(ipa) {
    var s = String(ipa || '').replace(/[.\s]/g, '');
    var out = [], i = 0, pend = 0;
    while (i < s.length) {
      var c = s.charAt(i);
      if (c === 'ˈ') { pend = 1; i++; continue; }
      if (c === 'ˌ') { pend = pend || 2; i++; continue; }
      if (c === 'ː') { if (out.length && out[out.length - 1].v) out[out.length - 1].long = true; i++; continue; }
      var hit = null;
      for (var k = 0; k < UNITS.length; k++) {
        if (s.substr(i, UNITS[k].length) === UNITS[k]) { hit = UNITS[k]; break; }
      }
      if (!hit) { i++; continue; }
      var seg = { u: hit === 'g' ? 'ɡ' : hit, v: !!VOWEL[hit], st: 0, long: false };
      if (seg.v && pend) { seg.st = pend; pend = 0; }
      out.push(seg);
      i += hit.length;
    }
    // LangIPA marks no stress on a word of one syllable; its vowel is the
    // stressed one all the same (the American length rule reads it).
    var nv = 0, marked = false;
    for (var q = 0; q < out.length; q++) if (out[q].v) { nv++; if (out[q].st) marked = true; }
    if (nv === 1 && !marked) for (var q2 = 0; q2 < out.length; q2++) if (out[q2].v) out[q2].st = 1;
    // An r-coloured vowel before another vowel lets its r go to the next
    // syllable: American camera /ˈkæmɚə/ is «кэ́мэрэ», 캐머러 — not «кэмэрэ» with
    // the r swallowed or 캐머어.
    for (var k2 = 0; k2 < out.length - 1; k2++) {
      if ((out[k2].u === 'ɚ' || out[k2].u === 'ɝ') && out[k2 + 1].v) {
        var rc = out[k2];
        out.splice(k2, 1, { u: rc.u === 'ɚ' ? 'ə' : 'ɜ', v: true, st: rc.st, long: false },
          { u: 'ɹ', v: false, st: 0, long: false });
        k2++;
      }
    }
    return out;
  }

  function nuclei(units) { var n = 0; for (var i = 0; i < units.length; i++) if (units[i].v) n++; return n; }

  var OBSTRUENT = { 'p': 1, 'b': 1, 't': 1, 'd': 1, 'k': 1, 'ɡ': 1, 'f': 1, 'v': 1, 'θ': 1,
    'ð': 1, 's': 1, 'z': 1, 'ʃ': 1, 'ʒ': 1, 'tʃ': 1, 'dʒ': 1 };
  var CORONAL_OBS = { 't': 1, 'd': 1, 's': 1, 'z': 1, 'ʃ': 1, 'ʒ': 1, 'tʃ': 1, 'dʒ': 1,
    'θ': 1, 'ð': 1, 'f': 1, 'v': 1 };

  /* Syllabic l / n: the schwa of a final -əl after an obstruent (table,
   * apple, little, people) and of a final -ən after a coronal or f/v
   * (button, garden, station, seven) is not a vowel in speech — «тэйбл»,
   * «батн», «стэйшн». After a nasal, l, r or a vowel it is (animal, open). */
  function syllabicSchwa(units, i) {
    var cur = units[i];
    if (!cur || cur.u !== 'ə' || cur.st) return false;
    var prev = units[i - 1], next = units[i + 1];
    if (!prev || prev.v || !next || (next.u !== 'l' && next.u !== 'n')) return false;
    // Only the word end or ONE inflection may follow (tables, buttons,
    // listened) — before a cluster the schwa is a vowel: student «стью́дэнт»,
    // important «импо́:тэнт», not «стьюднт».
    var rest = units.length - (i + 2);
    if (rest > 1) return false;
    if (rest === 1) {
      var u = units[i + 2].u;
      if (!(u === 's' || u === 'z' || u === 'd')) return false;
    }
    if (next.u === 'l') return !!OBSTRUENT[prev.u];
    return !!CORONAL_OBS[prev.u];
  }

  /* ── Cyrillic (ru, uk) ─────────────────────────────────────────── */
  var CYR = {
    ru: {
      V: { 'ɑ': 'а', 'æ': 'э', 'ʌ': 'а', 'ə': 'э', 'ɔ': 'о', 'ɒ': 'о', 'ɛ': 'э', 'e': 'э',
        'ɝ': 'ёр', 'ɚ': 'эр', 'ɜ': 'ё', 'ɪ': 'и', 'i': 'и', 'ʊ': 'у', 'u': 'у',
        'aɪ': 'ай', 'aʊ': 'ау', 'eɪ': 'эй', 'oʊ': 'оу', 'əʊ': 'оу', 'ɔɪ': 'ой',
        'eə': 'эа', 'ɪə': 'иэ', 'ʊə': 'уэ', 'aɪə': 'айэ', 'aʊə': 'ауэ', 'ɔɪə': 'ойэ' },
      C: { 'b': 'б', 'd': 'д', 'ð': 'з', 'f': 'ф', 'ɡ': 'г', 'h': 'х', 'k': 'к', 'l': 'л',
        'm': 'м', 'n': 'н', 'ŋ': 'нг', 'p': 'п', 'ɹ': 'р', 'r': 'р', 's': 'с', 'ʃ': 'ш',
        't': 'т', 'θ': 'с', 'v': 'в', 'w': 'у', 'j': 'й', 'z': 'з', 'ʒ': 'ж',
        'tʃ': 'ч', 'dʒ': 'дж' },
      // j + vowel: [word-initial / after a vowel, after a consonant]
      IOT: { 'u': ['ю', 'ью'], 'ʊ': ['ю', 'ью'], 'ʊə': ['юэ', 'ьюэ'],
        'ɑ': ['я', 'ья'], 'æ': ['я', 'ья'], 'ʌ': ['я', 'ья'],
        'ɛ': ['йе', 'ье'], 'e': ['йе', 'ье'], 'ə': ['йе', 'ье'], 'eə': ['йеа', 'ьеа'],
        'ɪ': ['йи', 'ьи'], 'i': ['йи', 'ьи'], 'ɪə': ['йиэ', 'ьиэ'],
        'ɔ': ['йо', 'ьо'], 'ɒ': ['йо', 'ьо'], 'oʊ': ['йоу', 'ьоу'], 'əʊ': ['йоу', 'ьоу'],
        'ɜ': ['йё', 'ьё'], 'ɝ': ['йёр', 'ьёр'], 'ɚ': ['йер', 'ьер'],
        'eɪ': ['йэй', 'ьэй'], 'aɪ': ['йай', 'ьай'], 'aʊ': ['йау', 'ьау'], 'ɔɪ': ['йой', 'ьой'] },
      LONG: ':',
      // stressed letters that carry the stress by themselves
      SELF_STRESSED: { 'ё': 1 },
      APPROX: { 'θ': 1, 'ð': 1, 'w': 1, 'æ': 1, 'ŋ': 1, 'ɜ': 1, 'ɝ': 1 },
      VOWEL_LETTERS: 'аэиоуыяеёю'
    },
    uk: {
      // Ukrainian и is /ɪ/ and і is /i/ — the KIT/FLEECE contrast Russian lacks.
      V: { 'ɑ': 'а', 'æ': 'е', 'ʌ': 'а', 'ə': 'е', 'ɔ': 'о', 'ɒ': 'о', 'ɛ': 'е', 'e': 'е',
        'ɝ': 'ьор', 'ɚ': 'ер', 'ɜ': 'ьо', 'ɪ': 'и', 'i': 'і', 'ʊ': 'у', 'u': 'у',
        'aɪ': 'ай', 'aʊ': 'ау', 'eɪ': 'ей', 'oʊ': 'оу', 'əʊ': 'оу', 'ɔɪ': 'ой',
        'eə': 'еа', 'ɪə': 'іе', 'ʊə': 'уе', 'aɪə': 'айе', 'aʊə': 'ауе', 'ɔɪə': 'ойе' },
      // Ukrainian г is /ɦ/ — the nearest thing to English h; English g is ґ.
      C: { 'b': 'б', 'd': 'д', 'ð': 'з', 'f': 'ф', 'ɡ': 'ґ', 'h': 'г', 'k': 'к', 'l': 'л',
        'm': 'м', 'n': 'н', 'ŋ': 'нг', 'p': 'п', 'ɹ': 'р', 'r': 'р', 's': 'с', 'ʃ': 'ш',
        't': 'т', 'θ': 'с', 'v': 'в', 'w': 'у', 'j': 'й', 'z': 'з', 'ʒ': 'ж',
        'tʃ': 'ч', 'dʒ': 'дж' },
      // [word-initial / after a vowel, after a consonant]; after a labial or р
      // the apostrophe form below (м'ю, комп'ю́тер) replaces the second one.
      IOT: { 'u': ['ю', 'ью'], 'ʊ': ['ю', 'ью'], 'ʊə': ['юе', 'ьюе'],
        'ɑ': ['я', 'ья'], 'æ': ['я', 'ья'], 'ʌ': ['я', 'ья'],
        'ɛ': ['є', 'ьє'], 'e': ['є', 'ьє'], 'ə': ['є', 'ьє'], 'eə': ['єа', 'ьєа'],
        'ɪ': ['ї', 'ї'], 'i': ['ї', 'ї'], 'ɪə': ['їе', 'їе'],
        'ɔ': ['йо', 'ьо'], 'ɒ': ['йо', 'ьо'], 'oʊ': ['йоу', 'ьоу'], 'əʊ': ['йоу', 'ьоу'],
        'ɜ': ['йо', 'ьо'], 'ɝ': ['йор', 'ьор'], 'ɚ': ['єр', 'ьєр'],
        'eɪ': ['єй', 'ьєй'], 'aɪ': ['яй', 'ьяй'], 'aʊ': ['яу', 'ьяу'], 'ɔɪ': ['йой', 'ьой'] },
      // NURSE after w, a vowel or at the start of a word: «ьо» needs a consonant
      // to soften, so work is «уе:к», not «уьо:к».
      NURSE_BARE: { 'ɜ': 'е', 'ɝ': 'ер' },
      // after a labial or р a Ukrainian iotated vowel takes the apostrophe (комп'ютер)
      APOS_AFTER: { 'б': 1, 'п': 1, 'в': 1, 'м': 1, 'ф': 1, 'р': 1 },
      LONG: ':',
      SELF_STRESSED: {},
      APPROX: { 'θ': 1, 'ð': 1, 'w': 1, 'æ': 1, 'ŋ': 1, 'ɜ': 1, 'ɝ': 1 },
      VOWEL_LETTERS: 'аеєиіїоуюя'
    }
  };

  function isLongUnit(seg, accent) {
    if (seg.long) return true;
    // American transcription carries no length mark; the tense close vowels
    // under stress are what keeps ship/sheep and full/fool apart.
    return accent === 'us' && seg.st > 0 && (seg.u === 'i' || seg.u === 'u');
  }

  function markStress(str, letters, selfStressed) {
    for (var i = 0; i < str.length; i++) {
      var ch = str.charAt(i);
      if (letters.indexOf(ch.toLowerCase()) >= 0) {
        if (selfStressed[ch.toLowerCase()]) return str;
        return str.slice(0, i + 1) + ACUTE + str.slice(i + 1);
      }
    }
    return str;
  }

  function cyrillic(units, locale, opts) {
    var T = CYR[locale], segs = [], multi = nuclei(units) > 1;
    for (var i = 0; i < units.length; i++) {
      var s = units[i], next = units[i + 1], prevSeg = segs.length ? segs[segs.length - 1] : null;
      var o, u = s.u, approx = !!T.APPROX[u], vowelSeg = s;

      if (u === 'j' && next && next.v && T.IOT[next.u]) {
        var afterCons = !!(prevSeg && prevSeg.o && !prevSeg.vowel && prevSeg.u !== 'j');
        var pair = T.IOT[next.u];
        o = afterCons ? pair[1] : pair[0];
        if (afterCons && T.APOS_AFTER && prevSeg && T.APOS_AFTER[prevSeg.o.slice(-1)]) o = "'" + pair[0];
        vowelSeg = next; u = 'j' + next.u; approx = !!T.APPROX[next.u];
        i++;
      } else if (u === 'w') {
        o = (next && (next.u === 'ʊ' || next.u === 'u')) ? 'в' : T.C.w;
      } else if (u === 'ŋ') {
        o = (next && (next.u === 'k' || next.u === 'ɡ')) ? 'н' : T.C['ŋ'];
        approx = !(next && (next.u === 'k' || next.u === 'ɡ'));
      } else if (s.v) {
        o = (opts.syllabic !== false && syllabicSchwa(units, i)) ? '' : (T.V[u] || '');
        if (T.NURSE_BARE && T.NURSE_BARE[u] && (!prevSeg || prevSeg.vowel || prevSeg.u === 'w')) o = T.NURSE_BARE[u];
      } else {
        o = T.C[u] || '';
      }

      var isV = vowelSeg.v;
      if (isV && o) {
        if (multi && vowelSeg.st === 1 && opts.stress !== false) o = markStress(o, T.VOWEL_LETTERS, T.SELF_STRESSED);
        if (opts.long !== false && isLongUnit(vowelSeg, opts.accent)) o += T.LONG;
      }
      segs.push({ o: o, u: u, vowel: isV, st: isV ? vowelSeg.st : 0, approx: approx && !!o,
        sound: SOUND_ID[vowelSeg === s ? s.u : vowelSeg.u] || null });
    }
    return segs;
  }

  /* ── English (en) — a dictionary respelling for native speakers ──── */
  /* Syllables joined by hyphens, the stressed one in capitals —
   * "KUHM-fer-tuh-buhl", "huh-LOH" — the convention readers of English
   * already know from dictionary.com and Wikipedia's respelling key. */
  var EN = {
    'æ': 'a', 'ɑ': 'ah', 'ʌ': 'uh', 'ə': 'uh', 'ɔ': 'aw', 'ɒ': 'o', 'ɛ': 'e', 'e': 'e',
    'ɝ': 'ur', 'ɚ': 'er', 'ɜ': 'ur', 'ɪ': 'i', 'i': 'ee', 'ʊ': 'uu', 'u': 'oo',
    'aɪ': 'y', 'aʊ': 'ow', 'eɪ': 'ay', 'oʊ': 'oh', 'əʊ': 'oh', 'ɔɪ': 'oy',
    'eə': 'air', 'ɪə': 'eer', 'ʊə': 'oor', 'aɪə': 'y-uh', 'aʊə': 'ow-uh', 'ɔɪə': 'oy-uh',
    'b': 'b', 'd': 'd', 'ð': 'th', 'f': 'f', 'ɡ': 'g', 'h': 'h', 'k': 'k', 'l': 'l',
    'm': 'm', 'n': 'n', 'ŋ': 'ng', 'p': 'p', 'ɹ': 'r', 'r': 'r', 's': 's', 'ʃ': 'sh',
    't': 't', 'θ': 'th', 'v': 'v', 'w': 'w', 'j': 'y', 'z': 'z', 'ʒ': 'zh',
    'tʃ': 'ch', 'dʒ': 'j'
  };
  function english(units, opts) {
    // Split at the stress marks and at the syllable joints LangIPA gave us.
    var bounds = opts.syllableStarts || null;
    var segs = [], multi = nuclei(units) > 1;
    for (var i = 0; i < units.length; i++) {
      var s = units[i], o = EN[s.u] || '';
      // price vowel standing alone reads "eye", not "y"
      if (s.u === 'aɪ' && (i === 0 || units[i - 1].v)) o = 'eye';
      // a lone short i or e closing a syllable reads long — write "ih" / "eh"
      if ((s.u === 'ɪ' || s.u === 'ɛ' || s.u === 'e') && (!units[i + 1] || units[i + 1].v ||
          (bounds && bounds.indexOf(i + 1) >= 0))) o = s.u === 'ɪ' ? 'ih' : 'eh';
      if (s.v && opts.syllabic !== false && syllabicSchwa(units, i)) o = 'uh';
      segs.push({ o: o, u: s.u, vowel: s.v, st: s.v ? s.st : 0, approx: false, sound: SOUND_ID[s.u] || null,
        brk: !!(bounds && i > 0 && bounds.indexOf(i) >= 0) });
    }
    if (multi && opts.stress !== false) {
      // capitalise the whole stressed syllable
      var start = 0, sylStressed = [];
      for (var k = 0; k <= segs.length; k++) {
        if (k === segs.length || (k > 0 && segs[k].brk)) {
          var st = 0;
          for (var m = start; m < k; m++) if (segs[m].vowel && segs[m].st === 1) st = 1;
          if (st) for (var q = start; q < k; q++) segs[q].o = segs[q].o.toUpperCase();
          sylStressed.push(st); start = k;
        }
      }
    }
    for (var z = 0; z < segs.length; z++) if (segs[z].brk) segs[z].o = '-' + segs[z].o;
    return segs;
  }

  /* ── Latin-alphabet readers (es, pt, de, fr, id) ─────────────────── */
  var LAT = {
    es: {
      // NURSE keeps its written r even in British: bird «berd», not «bed»
      V: { 'ɑ': 'a', 'æ': 'a', 'ʌ': 'a', 'ə': 'e', 'ɔ': 'o', 'ɒ': 'o', 'ɛ': 'e', 'e': 'e',
        'ɝ': 'er', 'ɚ': 'er', 'ɜ': 'er', 'ɪ': 'i', 'i': 'i', 'ʊ': 'u', 'u': 'u',
        'aɪ': 'ai', 'aʊ': 'au', 'eɪ': 'ei', 'oʊ': 'ou', 'əʊ': 'ou', 'ɔɪ': 'oi',
        'eə': 'ea', 'ɪə': 'ie', 'ʊə': 'ue', 'aɪə': 'aie', 'aʊə': 'aue', 'ɔɪə': 'oie' },
      C: { 'b': 'b', 'd': 'd', 'ð': 'd', 'f': 'f', 'ɡ': 'g', 'h': 'j', 'k': 'k', 'l': 'l',
        'm': 'm', 'n': 'n', 'ŋ': 'ng', 'p': 'p', 'ɹ': 'r', 'r': 'r', 's': 's', 'ʃ': 'sh',
        't': 't', 'θ': 'z', 'v': 'v', 'w': 'u', 'j': 'y', 'z': 's', 'ʒ': 'sh',
        'tʃ': 'ch', 'dʒ': 'y' },
      // g before e/i reads /x/ in Spanish — gue/gui keep it hard
      HARDG: { 'e': 'gu', 'i': 'gu' },
      // a Spanish reader cannot end a word on -bl / -tn: table «téibol», button «báten»
      SYLLABIC: false, SCHWA_L: 'o',
      // final ʤ devoices: judge «yach»
      FINAL: { 'dʒ': 'ch' },
      ACC: { 'a': 'á', 'e': 'é', 'i': 'í', 'o': 'ó', 'u': 'ú' },
      STRESS: 'acute', LONG: '',
      APPROX: { 'θ': 1, 'ð': 1, 'æ': 1, 'ʌ': 1, 'ɪ': 1, 'v': 1, 'z': 1, 'ʃ': 1, 'ʒ': 1, 'ɜ': 1 }
    },
    pt: {
      V: { 'ɑ': 'a', 'æ': 'é', 'ʌ': 'a', 'ə': 'â', 'ɔ': 'ó', 'ɒ': 'ó', 'ɛ': 'é', 'e': 'é',
        'ɝ': 'âr', 'ɚ': 'âr', 'ɜ': 'â', 'ɪ': 'i', 'i': 'i', 'ʊ': 'u', 'u': 'u',
        'aɪ': 'ai', 'aʊ': 'au', 'eɪ': 'ei', 'oʊ': 'ou', 'əʊ': 'ou', 'ɔɪ': 'ói',
        'eə': 'éa', 'ɪə': 'ia', 'ʊə': 'ua', 'aɪə': 'aia', 'aʊə': 'aua', 'ɔɪə': 'óia' },
      // Brazilian initial r is /h/ — rélou for hello is the classic key
      C: { 'b': 'b', 'd': 'd', 'ð': 'd', 'f': 'f', 'ɡ': 'g', 'h': 'r', 'k': 'k', 'l': 'l',
        'm': 'm', 'n': 'n', 'ŋ': 'ng', 'p': 'p', 'ɹ': 'r', 'r': 'r', 's': 's', 'ʃ': 'ch',
        't': 't', 'θ': 't', 'v': 'v', 'w': 'u', 'j': 'i', 'z': 'z', 'ʒ': 'j',
        'tʃ': 'tch', 'dʒ': 'dj' },
      HARDG: { 'e': 'gu', 'i': 'gu', 'é': 'gu' },
      // a Brazilian reader voices s between vowels — ss keeps it /s/
      SS: true,
      // the schwa of a final -əl is written o (apple «épol», the IUPI key);
      // a Brazilian reader adds a vowel to a bare final consonant anyway
      SYLLABIC: false, SCHWA_L: 'o',
      ACC: { 'a': 'á', 'e': 'ê', 'i': 'í', 'o': 'ô', 'u': 'ú', 'é': 'é', 'ó': 'ó', 'â': 'â' },
      STRESS: 'acute', LONG: '',
      APPROX: { 'θ': 1, 'ð': 1, 'h': 1, 'ɹ': 1, 'r': 1, 'æ': 1, 'ɜ': 1, 'ɝ': 1 }
    },
    de: {
      V: { 'ɑ': 'a', 'æ': 'ä', 'ʌ': 'a', 'ə': 'e', 'ɔ': 'o', 'ɒ': 'o', 'ɛ': 'e', 'e': 'e',
        'ɝ': 'ör', 'ɚ': 'er', 'ɜ': 'ö', 'ɪ': 'i', 'i': 'i', 'ʊ': 'u', 'u': 'u',
        'aɪ': 'ai', 'aʊ': 'au', 'eɪ': 'ej', 'oʊ': 'ou', 'əʊ': 'ou', 'ɔɪ': 'eu',
        'eə': 'ea', 'ɪə': 'ia', 'ʊə': 'ua', 'aɪə': 'aia', 'aʊə': 'aua', 'ɔɪə': 'eua' },
      C: { 'b': 'b', 'd': 'd', 'ð': 'd', 'f': 'f', 'ɡ': 'g', 'h': 'h', 'k': 'k', 'l': 'l',
        'm': 'm', 'n': 'n', 'ŋ': 'ng', 'p': 'p', 'ɹ': 'r', 'r': 'r', 's': 'ss', 'ʃ': 'sch',
        't': 't', 'θ': 's', 'v': 'w', 'w': 'u', 'j': 'j', 'z': 's', 'ʒ': 'sch',
        'tʃ': 'tsch', 'dʒ': 'dsch' },
      // German marks a long vowel in its own spelling: ie, ah, uh…
      LONGV: { 'i': 'ie', 'a': 'ah', 'u': 'uh', 'o': 'oh', 'ö': 'öh', 'e': 'eh' },
      STRESS: 'caps', LONG: 'spell',
      APPROX: { 'θ': 1, 'ð': 1, 'w': 1, 'æ': 1, 'ɹ': 1, 'r': 1 }
    },
    fr: {
      // ʌ is «eu» — cup «keup», love «leuv»: the classic French key
      V: { 'ɑ': 'a', 'æ': 'a', 'ʌ': 'eu', 'ə': 'e', 'ɔ': 'o', 'ɒ': 'o', 'ɛ': 'è', 'e': 'è',
        'ɝ': 'eur', 'ɚ': 'eur', 'ɜ': 'eu', 'ɪ': 'i', 'i': 'i', 'ʊ': 'ou', 'u': 'ou',
        'aɪ': 'aï', 'aʊ': 'aou', 'eɪ': 'éï', 'oʊ': 'ô', 'əʊ': 'ô', 'ɔɪ': 'oï',
        'eə': 'èe', 'ɪə': 'ie', 'ʊə': 'oue', 'aɪə': 'aïe', 'aʊə': 'aoue', 'ɔɪə': 'oïe' },
      C: { 'b': 'b', 'd': 'd', 'ð': 'z', 'f': 'f', 'ɡ': 'g', 'h': 'h', 'k': 'k', 'l': 'l',
        'm': 'm', 'n': 'n', 'ŋ': 'ng', 'p': 'p', 'ɹ': 'r', 'r': 'r', 's': 's', 'ʃ': 'ch',
        't': 't', 'θ': 's', 'v': 'v', 'w': 'ou', 'j': 'y', 'z': 'z', 'ʒ': 'j',
        'tʃ': 'tch', 'dʒ': 'dj' },
      HARDG: { 'e': 'gu', 'i': 'gu', 'è': 'gu', 'é': 'gu' },
      LONGV: { 'a': 'â', 'o': 'ô' },
      SS: true,
      SYLLABIC: false,
      // a French reader nasalises a vowel before n/m that closes the syllable
      // (man → /mɑ̃/); doubling the consonant keeps it oral: «mann»
      NASAL_GUARD: true,
      STRESS: 'caps', LONG: 'spell',
      APPROX: { 'θ': 1, 'ð': 1, 'h': 1, 'ɹ': 1, 'r': 1, 'ʌ': 1, 'ŋ': 1 }
    },
    id: {
      // é is /e/, plain e is the schwa — the é taling of the KBBI
      V: { 'ɑ': 'a', 'æ': 'é', 'ʌ': 'a', 'ə': 'e', 'ɔ': 'o', 'ɒ': 'o', 'ɛ': 'é', 'e': 'é',
        'ɝ': 'er', 'ɚ': 'er', 'ɜ': 'er', 'ɪ': 'i', 'i': 'i', 'ʊ': 'u', 'u': 'u',
        'aɪ': 'ai', 'aʊ': 'au', 'eɪ': 'éi', 'oʊ': 'ou', 'əʊ': 'ou', 'ɔɪ': 'oi',
        'eə': 'éa', 'ɪə': 'ia', 'ʊə': 'ua', 'aɪə': 'aie', 'aʊə': 'aue', 'ɔɪə': 'oie' },
      C: { 'b': 'b', 'd': 'd', 'ð': 'd', 'f': 'f', 'ɡ': 'g', 'h': 'h', 'k': 'k', 'l': 'l',
        'm': 'm', 'n': 'n', 'ŋ': 'ng', 'p': 'p', 'ɹ': 'r', 'r': 'r', 's': 's', 'ʃ': 'sy',
        't': 't', 'θ': 't', 'v': 'v', 'w': 'w', 'j': 'y', 'z': 'z', 'ʒ': 'sy',
        'tʃ': 'c', 'dʒ': 'j' },
      SYLLABIC: false,
      STRESS: 'caps', LONG: '',
      APPROX: { 'θ': 1, 'ð': 1, 'v': 1, 'ʒ': 1, 'æ': 1, 'ɜ': 1 }
    }
  };

  function latin(units, locale, opts) {
    var T = LAT[locale], segs = [], multi = nuclei(units) > 1;
    for (var i = 0; i < units.length; i++) {
      var s = units[i], next = units[i + 1], o;
      if (s.v) {
        var syl = T.SYLLABIC !== false && opts.syllabic !== false && syllabicSchwa(units, i);
        o = syl ? '' : (T.V[s.u] || '');
        if (T.SCHWA_L && s.u === 'ə' && !s.st && next && next.u === 'l' && i + 2 >= units.length) o = T.SCHWA_L;
        if (o && opts.long !== false && T.LONG === 'spell' && isLongUnit(s, opts.accent) && T.LONGV && T.LONGV[o]) o = T.LONGV[o];
        if (o && multi && s.st === 1 && opts.stress !== false && T.STRESS === 'acute' && T.ACC) {
          var ch = o.charAt(0);
          if (T.ACC[ch]) o = T.ACC[ch] + o.slice(1);
        }
      } else if (s.u === 'ŋ') {
        o = (next && (next.u === 'k' || next.u === 'ɡ')) ? 'n' : T.C['ŋ'];
        // French reads «ing» as the nasal of «saint» — sing is «sinng»
        if (T.NASAL_GUARD) o = o === 'ng' ? 'nng' : 'nn';
      } else {
        o = T.C[s.u] || '';
        if (T.SS && s.u === 's' && next && next.v && units[i - 1] && units[i - 1].v) o = 'ss';
        // German ss marks a voiceless s — pointless (and odd-looking) at the start of a word
        if (locale === 'de' && s.u === 's' && i === 0) o = 's';
        if (T.NASAL_GUARD && (s.u === 'n' || s.u === 'm') && (!next || !next.v) && segs.length) {
          var po = segs[segs.length - 1].o, pl = po.slice(-1).toLowerCase(), pl2 = po.slice(-2, -1).toLowerCase();
          if (pl && 'aeiouyéèêâô'.indexOf(pl) >= 0 && (!pl2 || 'aeiouéèêâô'.indexOf(pl2) < 0)) o = o + o;
        }
        if (T.FINAL && T.FINAL[s.u] && !next) o = T.FINAL[s.u];
        if (s.u === 'ɡ' && T.HARDG && next && next.v) {
          var nv = (T.V[next.u] || '').charAt(0);
          if (T.HARDG[nv]) o = T.HARDG[nv];
        }
      }
      segs.push({ o: o, u: s.u, vowel: s.v, st: s.v ? s.st : 0, approx: !!T.APPROX[s.u] && !!o,
        sound: SOUND_ID[s.u] || null });
    }
    if (multi && opts.stress !== false && T.STRESS === 'caps') capsStressedSyllable(segs, units);
    return segs;
  }

  /* Capitalise the stressed syllable (onset consonants + the vowel). */
  function capsStressedSyllable(segs, units) {
    var vi = -1;
    for (var i = 0; i < units.length; i++) if (units[i].v && units[i].st === 1) { vi = i; break; }
    if (vi < 0) return;
    var st = vi;
    // take back the consonants that begin this syllable (all of them up to the previous vowel,
    // leaving one for a preceding closed syllable when there are two or more)
    var j = vi - 1, cons = 0;
    while (j >= 0 && !units[j].v) { cons++; j--; }
    var take = j < 0 ? cons : Math.max(1, cons - 1);
    if (cons === 0) take = 0;
    st = vi - take;
    var end = vi + 1;
    // include the coda up to the next vowel's onset
    var k = vi + 1, codaCount = 0;
    while (k < units.length && !units[k].v) { codaCount++; k++; }
    end = vi + 1 + (k >= units.length ? codaCount : Math.max(0, codaCount - 1));
    for (var q = st; q < end; q++) segs[q].o = segs[q].o.toUpperCase();
  }

  /* ── Korean — Hangul blocks, after the loanword orthography ──────── */
  var KO_L = { 'b': 7, 'p': 17, 'd': 3, 't': 16, 'ɡ': 0, 'k': 15, 'm': 6, 'n': 2,
    'ɹ': 5, 'r': 5, 'l': 5, 'f': 17, 'v': 7, 's': 9, 'z': 12, 'θ': 9, 'ð': 3, 'ʃ': 9,
    'ʒ': 12, 'h': 18, 'tʃ': 14, 'dʒ': 12, 'ŋ': 11 };
  // jungseong indexes: ㅏ0 ㅐ1 ㅑ2 ㅒ3 ㅓ4 ㅔ5 ㅕ6 ㅖ7 ㅗ8 ㅘ9 ㅙ10 ㅚ11 ㅛ12 ㅜ13 ㅝ14 ㅞ15 ㅟ16 ㅠ17 ㅡ18 ㅢ19 ㅣ20
  var KO_V = { 'ɑ': 0, 'æ': 1, 'ʌ': 4, 'ə': 4, 'ɔ': 8, 'ɒ': 8, 'ɛ': 5, 'e': 5, 'ɪ': 20,
    'i': 20, 'ʊ': 13, 'u': 13, 'ɜ': 4, 'ɝ': 4, 'ɚ': 4 };
  var KO_JV = { 0: 2, 1: 3, 4: 6, 5: 7, 8: 12, 13: 17, 20: 20 };      // j + vowel
  var KO_WV = { 0: 9, 1: 10, 4: 14, 5: 15, 8: 14, 13: 13, 20: 16 };    // w + vowel
  var KO_DIPH = { 'eɪ': ['e', 'i'], 'aɪ': ['ɑ', 'i'], 'ɔɪ': ['ɔ', 'i'], 'aʊ': ['ɑ', 'u'],
    'oʊ': ['ɔ'], 'əʊ': ['ɔ'], 'eə': ['e', 'ə'], 'ɪə': ['i', 'ə'], 'ʊə': ['u', 'ə'],
    'aɪə': ['ɑ', 'i', 'ə'], 'aʊə': ['ɑ', 'u', 'ə'], 'ɔɪə': ['ɔ', 'i', 'ə'] };
  var KO_FINAL = { 'n': 4, 'ŋ': 21, 'l': 8, 'm': 16, 'p': 17, 'k': 1, 't': 19 };
  var KO_SHORT = { 'æ': 1, 'ɛ': 1, 'e': 1, 'ɪ': 1, 'ʌ': 1, 'ʊ': 1, 'ɒ': 1, 'ə': 1 };
  function koBlock(L, V, T) { return String.fromCharCode(0xAC00 + (L * 21 + V) * 28 + (T || 0)); }

  /* Blocks follow the loanword orthography (외래어 표기법) where it is a RULE
   * rather than a spelling memory: a stop closes a short vowel (cat 캣, book
   * 북) but not a long one (cake 케이크); intervocalic l doubles (jelly 젤리);
   * C + l takes ㄹ into the stranded syllable (play 플레이, English 잉글리시);
   * final ʃ is 시 and final dʒ/tʃ are 지/치 (fish 피시, orange 오렌지); sw, tw,
   * dw keep the consonant apart (sweater 스웨터) while kw, gw, hw merge
   * (queen 퀸). Where the standard form follows the English SPELLING of an
   * unstressed vowel (banana 바나나, tennis 테니스) this stays phonetic —
   * 버내너 is how the word sounds, and sound is what the tool is for. */
  var KO_APPROX = { 'θ': 1, 'ð': 1, 'f': 1, 'v': 1, 'z': 1, 'ɹ': 1, 'r': 1, 'ʒ': 1, 'æ': 1, 'ɜ': 1, 'ɝ': 1 };
  function korean(units, opts) {
    var ph = [];
    for (var t = 0; t < units.length; t++) {
      var u = units[t];
      if (u.v && KO_DIPH[u.u]) {
        KO_DIPH[u.u].forEach(function (x) { ph.push({ u: x, v: true, short: false }); });
      } else if (u.v && u.u === 'ə' && opts.syllabic !== false && syllabicSchwa(units, t)) {
        ph.push({ u: 'ɨ', v: true, short: true });
      } else if (u.u === 'ɝ' || u.u === 'ɚ') {
        ph.push({ u: 'ʌ', v: true, short: false });
      } else if (u.u === 't' && units[t + 1] && units[t + 1].u === 's') {
        ph.push({ u: 'ts', v: false }); t++;              // /ts/ → ㅊ: cats 캐츠
      } else {
        ph.push({ u: u.u, v: u.v, short: !!KO_SHORT[u.u] });
      }
    }
    var LJ = { 'ts': 14 };
    function initialOf(x) { return LJ[x] != null ? LJ[x] : (KO_L[x] != null ? KO_L[x] : 11); }
    var out = '', onset = null, onsetPh = null, glide = null;
    function flush(nextPh) {
      // a consonant with no vowel of its own gets ㅡ — or ㅣ/ㅠ after ʃ,
      // ㅣ after a final ʤ/ʧ — and ㄹ when an l follows it in the cluster
      var v = 18, fin = 0;
      if (onsetPh === 'ʃ') v = nextPh ? 17 : 20;
      else if (!nextPh && (onsetPh === 'dʒ' || onsetPh === 'tʃ' || onsetPh === 'ʒ')) v = 20;
      if (nextPh === 'l') fin = 8;
      out += koBlock(onset, v, fin);
      onset = null; onsetPh = null;
    }
    for (var i = 0; i < ph.length; i++) {
      var p = ph[i];
      if (p.v) {
        var vi = p.u === 'ɨ' ? 18 : (KO_V[p.u] != null ? KO_V[p.u] : 18);
        var g = glide || (onsetPh === 'ʃ' ? 'j' : null);
        if (g === 'j' && KO_JV[vi] != null) vi = KO_JV[vi];
        if (g === 'w' && KO_WV[vi] != null) vi = KO_WV[vi];
        glide = null;
        var L = onset != null ? onset : 11;
        onset = null; onsetPh = null;
        var T = 0, nx = ph[i + 1], nx2 = ph[i + 2];
        var nx2cons = !nx2 || (!nx2.v && nx2.u !== 'j' && nx2.u !== 'w');
        if (nx && nx.u === 'ŋ') {
          T = 21; i++;                                   // ŋ is never an onset: singer 싱어
        } else if (nx && !nx.v && KO_FINAL[nx.u] != null && nx2cons) {
          var isStop = nx.u === 'p' || nx.u === 'k' || nx.u === 't';
          if (!isStop || p.short) { T = KO_FINAL[nx.u]; i++; }
        } else if (nx && nx.u === 'l' && nx2 && nx2.v) {
          T = 8;                                         // hello 헬로, jelly 젤리
        }
        out += koBlock(L, vi, T);
      } else if (p.u === 'j') {
        glide = 'j';                                     // music 뮤, computer 퓨
      } else if (p.u === 'w') {
        if (onset != null && !(onsetPh === 'k' || onsetPh === 'ɡ' || onsetPh === 'h')) flush('w');
        glide = 'w';
      } else {
        if (onset != null) flush(p.u);
        onset = initialOf(p.u); onsetPh = p.u;
      }
    }
    if (onset != null) flush(null);
    var sounds = [], seen = {};
    for (var q = 0; q < units.length; q++) {
      var sid = KO_APPROX[units[q].u] && SOUND_ID[units[q].u];
      if (sid && !seen[sid]) { seen[sid] = 1; sounds.push(sid); }
    }
    var seg = { o: out, u: '', vowel: false, st: 0, approx: false, sound: null };
    seg.sounds = sounds;
    return [seg];
  }

  /* ── Arabic — letters for every vowel, the way learners write it ──── */
  /* The practice to match is the one Arabic speakers use online for English
   * (هاو ار يو، كمبيوتر، ثينك): every vowel gets a letter, a medial schwa gets
   * none, a final one gets alif (banana بنانا); the centring diphthongs keep
   * their written r (here هير) because a reader expects it there. θ and ð are
   * exact (ث ذ); p, v, g, ʒ, ŋ are the nearest Arabic letters. */
  var AR = {
    'ɑ': 'ا', 'æ': 'ا', 'ʌ': 'ا', 'ə': '', 'ɔ': 'و', 'ɒ': 'و', 'ɛ': 'ي', 'e': 'ي',
    'ɝ': 'ير', 'ɚ': 'ر', 'ɜ': 'ير', 'ɪ': 'ي', 'i': 'ي', 'ʊ': 'و', 'u': 'و',
    'aɪ': 'اي', 'aʊ': 'او', 'eɪ': 'ي', 'oʊ': 'و', 'əʊ': 'و', 'ɔɪ': 'وي',
    'eə': 'ير', 'ɪə': 'ير', 'ʊə': 'ور', 'aɪə': 'ايا', 'aʊə': 'اوا', 'ɔɪə': 'ويا',
    'b': 'ب', 'd': 'د', 'ð': 'ذ', 'f': 'ف', 'ɡ': 'غ', 'h': 'ه', 'k': 'ك', 'l': 'ل',
    'm': 'م', 'n': 'ن', 'ŋ': 'نغ', 'p': 'ب', 'ɹ': 'ر', 'r': 'ر', 's': 'س', 'ʃ': 'ش',
    't': 'ت', 'θ': 'ث', 'v': 'ف', 'w': 'و', 'j': 'ي', 'z': 'ز', 'ʒ': 'ج',
    'tʃ': 'تش', 'dʒ': 'ج'
  };
  var AR_APPROX = { 'p': 1, 'v': 1, 'ɡ': 1, 'ʒ': 1, 'ŋ': 1, 'ɛ': 1, 'e': 1, 'æ': 1 };
  function arabic(units) {
    var segs = [];
    for (var i = 0; i < units.length; i++) {
      var s = units[i], o = AR[s.u] || '';
      var prev = segs.length ? segs[segs.length - 1].o : '';
      if (s.u === 'ə' && i === units.length - 1) o = 'ا';          // final schwa: banana بنانا
      // y + a front vowel is one ي (yes يس), w + a back vowel keeps both (wood وود)
      if (s.v && o === 'ي' && prev === 'ي' && units[i - 1] && units[i - 1].u === 'j') o = '';
      if (s.v && segs.length === 0) {
        // a word cannot begin with a vowel letter alone — it rides on alif
        // (a vowel spelled with alif already — aɪ اي, aʊ او — takes the madda, not a second alif: I آي, out آوت)
        if (o === '') o = 'آ';
        else if (o.charAt(0) === 'ا') o = 'آ' + o.slice(1);
        else o = 'ا' + o;
      }
      if (s.u === 'ŋ' && units[i + 1] && (units[i + 1].u === 'k' || units[i + 1].u === 'ɡ')) o = 'ن';
      segs.push({ o: o, u: s.u, vowel: s.v, st: s.v ? s.st : 0, approx: !!AR_APPROX[s.u] && !!o,
        sound: SOUND_ID[s.u] || null });
    }
    return segs;
  }

  /* ── metadata ──────────────────────────────────────────────────── */
  var META = {
    ru: { tier: 'curated', accuracy: 'good', script: 'Cyrl' },
    uk: { tier: 'curated', accuracy: 'good', script: 'Cyrl' },
    en: { tier: 'curated', accuracy: 'good', script: 'Latn' },
    es: { tier: 'curated', accuracy: 'good', script: 'Latn' },
    de: { tier: 'curated', accuracy: 'good', script: 'Latn' },
    pt: { tier: 'curated', accuracy: 'fair', script: 'Latn' },
    fr: { tier: 'curated', accuracy: 'fair', script: 'Latn' },
    id: { tier: 'curated', accuracy: 'fair', script: 'Latn' },
    ko: { tier: 'curated', accuracy: 'fair', script: 'Hang' },
    ar: { tier: 'coarse', accuracy: 'rough', script: 'Arab' }
  };

  function gapsOf(segs) {
    var seen = {}, out = [];
    if (segs.length === 1 && segs[0].sounds) return segs[0].sounds.slice();
    for (var i = 0; i < segs.length; i++) {
      if (segs[i].approx && segs[i].sound && !seen[segs[i].sound]) { seen[segs[i].sound] = 1; out.push(segs[i].sound); }
    }
    return out;
  }

  /* ── public API ────────────────────────────────────────────────── */
  /* respell(ipa, locale, opts) → {text, plain, segs, gaps, sounds, tier, accuracy, locale}
   * opts: stress (true), long (true), syllabic (true), accent 'gb'|'us',
   *       syllableStarts (en only — unit indexes where a syllable begins). */
  function respell(ipa, locale, opts) {
    opts = opts || {};
    locale = String(locale || 'ru').slice(0, 2);
    if (!META[locale]) locale = 'ru';
    var units = parse(ipa), segs;
    if (CYR[locale]) segs = cyrillic(units, locale, opts);
    else if (locale === 'en') segs = english(units, opts);
    else if (LAT[locale]) segs = latin(units, locale, opts);
    else if (locale === 'ko') segs = korean(units, opts);
    else segs = arabic(units, opts);
    var text = '';
    for (var i = 0; i < segs.length; i++) text += segs[i].o;
    var sounds = gapsOf(segs);
    return {
      text: text,
      plain: text.replace(new RegExp(ACUTE, 'g'), '').replace(/:/g, ''),
      segs: segs,
      locale: locale,
      tier: META[locale].tier,
      accuracy: META[locale].accuracy,
      sounds: sounds,
      // v1 shape: sound families the target script only approximates
      gaps: sounds
    };
  }

  /* respellResult(ipaResult, locale, opts) — the same from a LangIPA result,
   * which also knows where each syllable starts (the en scheme needs it). */
  function respellResult(res, locale, opts) {
    opts = opts || {};
    if (!res) return respell('', locale, opts);
    if (String(locale).slice(0, 2) === 'en' && res.syllables && res.syllables.length > 1) {
      var starts = [], n = 0;
      for (var s = 0; s < res.syllables.length; s++) {
        if (s > 0) starts.push(n);
        n += parse(res.syllables[s]).length;
      }
      var o2 = {}; for (var k in opts) o2[k] = opts[k];
      o2.syllableStarts = starts;
      opts = o2;
    }
    return respell(res.ipa, locale, opts);
  }

  /* Upper-case the first letter when the English word was capitalised. */
  function capitalize(text, token) {
    if (!token || !/^[A-Z]/.test(token) || !text) return text;
    var i = 0;
    while (i < text.length && /['\-\s]/.test(text.charAt(i))) i++;
    return text.slice(0, i) + text.charAt(i).toUpperCase() + text.slice(i + 1);
  }

  return {
    respell: respell,
    respellResult: respellResult,
    capitalize: capitalize,
    parse: parse,
    tokenize: function (ipa) { return parse(ipa).map(function (s) { return s.u; }); },
    supportedLocales: function () { return Object.keys(META); },
    scriptOf: function (l) { return (META[l] && META[l].script) || null; },
    tierFor: function (l) { return (META[l] && META[l].tier) || null; },
    accuracyFor: function (l) { return (META[l] && META[l].accuracy) || null; },
    /* How the stressed syllable is shown: 'acute' (ка́мфтэбл), 'caps' (KUHMF-tuh-buhl,
     * KAMFtebl) or 'none' (Hangul, Arabic). A page capitalises a sentence's first
     * word only in an 'acute' script — in a 'caps' one capitals ARE the stress. */
    /* Whether length is written at all: 'colon' (ши:п), 'spell' (German schiep,
     * French â) or 'none' — Spanish, Portuguese, Indonesian, Hangul and Arabic
     * letters have no way to tell sheep from ship, so a page hides the switch. */
    lengthStyleOf: function (l) {
      if (CYR[l]) return 'colon';
      if (LAT[l]) return LAT[l].LONG === 'spell' ? 'spell' : 'none';
      return 'none';
    },
    stressStyleOf: function (l) {
      if (CYR[l]) return 'acute';
      if (l === 'en') return 'caps';
      if (LAT[l]) return LAT[l].STRESS === 'acute' ? 'acute' : 'caps';
      return 'none';
    },
    ACUTE: ACUTE
  };
});
